/**
 * Host for Location Tags management (#999 Stories 1.2 / 3.12 · UX-DR8 · QA #5–#11).
 * Web: centered dialog. Native: full-screen destination with Vertiv Header chrome
 * (no new navigator route — avoids app-wide wiring churn while meeting UX-DR8).
 */

import React from 'react';
import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import { Header } from '../Header';
import { SafeBottomArea } from '../SafeBottomArea';
import { LocationTagsManagementScreen } from './LocationTagsManagementScreen';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';

export interface LocationTagsManagementModalProps {
  visible: boolean;
  onClose: () => void;
  requestId: string;
  canMutate: boolean;
  permissionsLoading?: boolean;
  mode?: 'server' | 'draft';
}

export const LocationTagsManagementModal: React.FC<
  LocationTagsManagementModalProps
> = ({
  visible,
  onClose,
  requestId,
  canMutate,
  permissionsLoading = false,
  mode = 'server',
}) => {
  const isWeb = Platform.OS === 'web';

  if (isWeb) {
    return (
      <Modal
        visible={visible}
        transparent
        animationType="fade"
        onRequestClose={onClose}
        statusBarTranslucent
      >
        <Pressable
          style={styles.backdrop}
          onPress={onClose}
          testID="pd-location-tags-dialog-backdrop"
        >
          <Pressable
            style={styles.panel}
            onPress={() => undefined}
            testID="pd-location-tags-dialog-panel"
          >
            <LocationTagsManagementScreen
              active={visible}
              onClose={onClose}
              requestId={requestId}
              canMutate={canMutate}
              permissionsLoading={permissionsLoading}
              mode={mode}
              layout="dialog"
              showLocalChrome
            />
          </Pressable>
        </Pressable>
      </Modal>
    );
  }

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={onClose}
      testID="pd-location-tags-native-modal"
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.fullscreenRoot}
        testID="pd-location-tags-keyboard-avoid"
      >
        <View
          style={styles.fullscreenColumn}
          testID="pd-location-tags-fullscreen-host"
        >
          <Header
            title="Location Tags"
            disableMenu
            rightElement={
              <TouchableOpacity
                onPress={onClose}
                testID="pd-location-tags-modal-close"
                accessibilityRole="button"
                accessibilityLabel="Done"
                style={styles.doneButton}
              >
                <Text style={styles.doneText}>Done</Text>
              </TouchableOpacity>
            }
          />
          <SafeBottomArea
            testID="pd-location-tags-safe-bottom"
            backgroundColor={colors.backgroundPrimary}
            style={styles.fullscreenBody}
          >
            <LocationTagsManagementScreen
              active={visible}
              onClose={onClose}
              requestId={requestId}
              canMutate={canMutate}
              permissionsLoading={permissionsLoading}
              mode={mode}
              layout="fullscreen"
              showLocalChrome={false}
            />
          </SafeBottomArea>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    justifyContent: 'center',
    padding: spacing.lg,
    ...Platform.select({
      web: {
        // Keep dialog stacking below portal-range web toasts (App Toaster).
        zIndex: 1000,
      },
      default: {},
    }),
  },
  panel: {
    alignSelf: 'center',
    width: '100%',
    maxWidth: 480,
    maxHeight: '80%',
    minHeight: 0,
    overflow: 'hidden',
    backgroundColor: colors.backgroundPrimary,
    borderRadius: 12,
    padding: spacing.lg,
    ...Platform.select({
      web: {
        display: 'flex' as unknown as undefined,
        flexDirection: 'column' as unknown as undefined,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.18)' as unknown as undefined,
      },
      default: {
        elevation: 8,
      },
    }),
  },
  fullscreenRoot: {
    flex: 1,
    backgroundColor: colors.backgroundPrimary,
  },
  fullscreenColumn: {
    flex: 1,
    minHeight: 0,
  },
  fullscreenBody: {
    flex: 1,
    minHeight: 0,
  },
  doneButton: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  doneText: {
    color: colors.primary,
    fontWeight: '600',
    fontSize: 16,
  },
});
